import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { TweenMax, TimelineMax } from 'gsap';
import moment from 'moment';
import debounce from 'lodash/debounce';
import ReactMarkdown from 'react-markdown';
import createDebug from 'debug';

import withUrlGenerator from '../../lib/withUrlGenerator';
import withTranslations from '../../lib/withTranslations';
import ReactDraw from '../../vendor/react-draw/CanvasThree';
import Header from '../partials/Header';
import Splash from '../partials/Splash';
import Home from '../pages/Home';
import Count from '../../lib/Count';
import ClockAudio from '../../lib/ClockAudio';

import { updateData as updateDataActions } from '../../actions/CountActions';

const debug = createDebug('app:main');

const propTypes = {
    updateCountData: PropTypes.func.isRequired,
    count: PropTypes.number.isRequired,
    children: PropTypes.node,
    urlGenerator: PropTypes.shape({
        route: PropTypes.func,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }).isRequired,
};

const defaultProps = {
    children: null,
};

class MainLayout extends Component {
    constructor(props) {
        super(props);

        this.onResize = debounce(this.onResize.bind(this), 200);
        this.onTick = this.onTick.bind(this);
        this.onCanvasReady = this.onCanvasReady.bind(this);
        this.onCanvasTickReady = this.onCanvasTickReady.bind(this);
        this.onCountChange = this.onCountChange.bind(this);
        this.onClockReady = this.onClockReady.bind(this);
        this.onAudioLoaded = this.onAudioLoaded.bind(this);
        this.onOrientationChange = this.onOrientationChange.bind(this);

        this.count = null;
        this.audio = null;

        this.refHome = null;
        this.refModal = null;
        this.refSplash = null;
        this.refSplashContainer = null;

        this.state = {
            renderer: null,
            scene: null,
            camera: null,
            canvasTick: null,
            width: window.innerWidth,
            height: window.innerHeight,
            clockReady: false,
            audioLoaded: false,
            count: null,
            percent: 0,
        };
    }

    componentDidMount() {
        const { urlGenerator, location } = this.props;
        window.addEventListener('resize', this.onResize);
        TweenMax.ticker.addEventListener('tick', this.onTick);

        const audioSprites = [
            '/audio/sprite_2.mp3',
            '/audio/sprite_3.mp3',
            '/audio/sprite_5.mp3',
            '/audio/sprite_6.mp3',
            '/audio/sprite_7.mp3',
            '/audio/sprite_8.mp3',
            '/audio/sprite_9.mp3',
            '/audio/sprite_10.mp3',
            '/audio/sprite_11.mp3',
        ];
        const audioSprite = audioSprites[Math.floor(Math.random() * audioSprites.length)];
        this.audio = new ClockAudio({
            src: [audioSprite],
            duration: 112824,
            sprite: {
                // count_1: [0, 112824, false],
                // count_40: [115 * 1000, 112824, false],
                // count_100: [230 * 1000, 112824, false],
                // count_500: [350 * 1000, 112824, false],
                count_1: [0, 112824, false],
                count_100: [115 * 1000, 112824, false],
                count_500: [230 * 1000, 112824, false],
                count_800: [350 * 1000, 112824, false],
            },
            count: 1,
            clockServer: '/api/clock',
            startTime: moment.utc([2017, 0, 1]),
        });
        this.audio.on('load', this.onAudioLoaded);
        this.audio.on('ready', this.onClockReady);

        this.count = new Count();
        this.count.on('change', this.onCountChange);
        this.count.start();

        const isHome = urlGenerator.route('home') === location.pathname;
        TweenMax.set(this.refHome, {
            autoAlpha: isHome ? 1 : 0,
        });
        TweenMax.set(this.refModal, {
            autoAlpha: isHome ? 0 : 1,
        });

        const timeline = new TimelineMax();
        timeline.add(this.refSplash.getTimeline().paused(false), '+=2');
        timeline.to(this.refSplashContainer, 1, {
            autoAlpha: 0,
        });

        window.addEventListener('orientationchange', this.onOrientationChange, false);
        if (typeof screen.orientation !== 'undefined' && screen.orientation.angle !== 0) {
            document.body.classList.add('landscape');
        }
    }

    componentWillReceiveProps(nextProps) {
        const countChanged = this.props.count !== nextProps.count;
        if (countChanged && this.props.count === -1) {
            this.setState(({ percent }) => ({
                percent: percent + 33,
            }));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const sceneChanged = prevState.scene !== this.state.scene;
        if (sceneChanged) {
            if (prevState.scene !== null) {
                this.destroyScene(prevState.scene);
            }
            this.createScene(this.state.scene);
        }

        const sizeChanged = (
            prevState.width !== this.state.width ||
            prevState.height !== this.state.height
        );
        if ((sceneChanged || sizeChanged) && this.state.scene) {
            this.resizeScene(this.state.scene, this.state.width, this.state.height);
        }

        const pathChanged = prevProps.location.pathname !== this.props.location.pathname;
        if (pathChanged) {
            const isHome = this.props.urlGenerator.route('home') === this.props.location.pathname;
            TweenMax.to(this.refHome, 0.2, {
                autoAlpha: isHome ? 1 : 0,
            });
            TweenMax.to(this.refModal, 0.2, {
                autoAlpha: isHome ? 0 : 1,
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onResize);
        TweenMax.ticker.removeEventListener('tick', this.onTick);

        if (this.state.scene) {
            this.destroyScene(this.state.scene);
        }

        if (this.count !== null) {
            this.count.off('change', this.onCountChange);
            this.count.stop();
            this.count = null;
        }

        if (this.audio !== null) {
            this.audio.off('load', this.onAudioLoaded);
            this.audio.off('ready', this.onClockReady);
            this.audio.destroy();
            this.audio = null;
        }

        window.removeEventListener('orientationchange', this.onOrientationChange);
    }

    onCanvasReady(renderer, scene, camera) {
        renderer.setClearColor(0x00091B, 1);
        camera.position.set(0, 0, 0);
        this.setState({
            renderer,
            scene,
            camera,
        });
    }

    onCanvasTickReady(tick) {
        this.setState({
            canvasTick: tick,
        });
    }

    onResize() {
        if (typeof screen.orientation !== 'undefined' && screen.orientation.angle !== 0) {
            document.body.classList.add('landscape');
        } else {
            document.body.classList.remove('landscape');
        }
        this.setState({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    }

    // eslint-disable-next-line
    onOrientationChange() {
        if (screen.orientation.angle !== 0) {
            document.body.classList.add('landscape');
        } else {
            document.body.classList.remove('landscape');
        }
    }

    onTick() {
        this.tick();
    }

    onClockReady() {
        this.setState(({ percent }) => ({
            clockReady: true,
            percent: percent + 34,
        }));
    }

    onAudioLoaded() {
        this.setState(({ percent }) => ({
            audioLoaded: true,
            percent: percent + 33,
        }));
    }

    onCountChange(data) {
        debug(`Count changed: ${data.count}`);
        this.props.updateCountData(data);
    }

    /* eslint-disable no-param-reassign */
    createScene(scene) {
        scene.add(this.state.camera);
    }

    // eslint-disable-next-line class-methods-use-this
    resizeScene() {

    }

    // eslint-disable-next-line class-methods-use-this
    destroyScene() {

    }
    /* eslint-enable no-param-reassign */

    tick() {
        if (this.state.canvasTick !== null) {
            this.state.canvasTick();
        }
    }

    render() {
        const { children, t } = this.props;
        const { width, height, scene, camera, percent } = this.state;
        return (
            <div className="layout">
                <div className="container-header">
                    <Header />
                </div>
                <div className="container-content">
                    <div
                        className="container-page"
                        ref={(ref) => { this.refHome = ref; }}
                    >
                        <Home
                            width={width}
                            height={height}
                            scene={scene}
                            camera={camera}
                            percent={percent}
                            audio={this.audio}
                        />
                    </div>
                    <div
                        className="container-page container-modal"
                        ref={(ref) => { this.refModal = ref; }}
                    >
                        { children !== null ? React.cloneElement(children, {
                            width,
                            height,
                        }) : null }
                    </div>
                </div>
                <div className="container-canvas">
                    <div className="canvas-texture" />
                    <ReactDraw
                        width={width}
                        height={height}
                        tick={false}
                        onReady={this.onCanvasReady}
                        onTickReady={this.onCanvasTickReady}
                    />
                </div>
                <div
                    className="container-page container-splash"
                    ref={(ref) => { this.refSplashContainer = ref; }}
                >
                    <Splash
                        width={width}
                        height={height}
                        ref={(ref) => { this.refSplash = ref; }}
                    />
                </div>
                <div className="orientation">
                    <div className="table fullscreen">
                        <div className="cell middle">
                            <ReactMarkdown
                                source={t('rotate')}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

}

MainLayout.propTypes = propTypes;
MainLayout.defaultProps = defaultProps;

const mapStateToProps = state => ({
    count: state.count.count,
});

const mapDispatchToProps = dispatch => ({
    updateCountData: data => dispatch(updateDataActions(data)),
});

const enhancer = compose(
    withUrlGenerator(),
    withTranslations(),
    connect(mapStateToProps, mapDispatchToProps),
);
export default enhancer(MainLayout);



// WEBPACK FOOTER //
// ./components/layouts/Main.jsx